import { UnstyledButton, useHover } from '@sparemin/blockhead';
import React from 'react';
import { useEditSceneModalSlotsOverlayContext } from '../../EditSceneModalSlotsOverlayContext';
import {
  useControlPanelMode,
  useEditSceneModalStateContext,
  useIsInteractionDisabled,
  useSelectSlot,
  useSlot,
  useSlotDrag,
  useSlotSwapDnd,
  useSlotZoom,
} from '../../state';
import { block, getSlotBorderRadius, getSlotRect } from '../../utils';

type EditSceneOverlaySlotProps = {
  slotIndex: number;
};

/**
 * A "slot" in the EditSceneOverlay is a single overlay that covers a slot at the
 * given index.
 *
 * The EditSceneOverlay is placed over the entire canvas and contains these
 * EditSceneOverlaySlots.
 *
 * The slot is responsible for detecting clicks, rendering drag grid lines, detecting
 * drag, etc.
 */
const EditSceneOverlaySlot: React.FC<EditSceneOverlaySlotProps> = ({
  slotIndex,
}) => {
  const {
    state: { previewDimensions },
  } = useEditSceneModalStateContext();

  const { isInteractionDisabled } = useIsInteractionDisabled();

  const { getOverlayRef } = useEditSceneModalSlotsOverlayContext();

  const { controlPanelMode } = useControlPanelMode();

  const slot = useSlot(slotIndex);
  const rect = !slot ? undefined : getSlotRect(slot, previewDimensions);

  const { hoverProps, isHovered } = useHover({
    isDisabled: controlPanelMode === 'adjust-crop' || isInteractionDisabled,
  });

  const { isSelected, selectSlot } = useSelectSlot({ slotIndex });

  const { draggableProps } = useSlotDrag({ slotIndex });
  const { zoomProps } = useSlotZoom({ slotIndex });
  const {
    ref: swapDndRef,
    isDragging: isSlotDragging,
    isOver: isDropTarget,
  } = useSlotSwapDnd({ slotIndex });

  return (
    <div
      className={block('slots-overlay-slot', {
        dragging: isSlotDragging,
        target: isDropTarget,
      })}
      style={{
        borderRadius: getSlotBorderRadius(rect, previewDimensions),
        ...rect,
      }}
      ref={getOverlayRef(slotIndex)}
    >
      <UnstyledButton
        as="div"
        className={block('slots-overlay-slot-button', { selected: isSelected })}
        data-hovered={isHovered}
        onPress={selectSlot}
        ref={swapDndRef}
        {...hoverProps}
      >
        {isSelected && controlPanelMode === 'adjust-crop' && (
          <div className={block('slots-overlay-slot-crop-grid')} />
        )}
      </UnstyledButton>

      {controlPanelMode === 'adjust-crop' && isSelected && (
        <div
          className={block('slots-overlay-slot-drag-layer')}
          {...draggableProps}
          {...zoomProps}
        />
      )}
    </div>
  );
};

export default EditSceneOverlaySlot;
